import React from 'react';
import { AnimateOnScroll } from './AnimateOnScroll';
function HexIcon() {
  return <svg width="16" height="18" viewBox="0 0 16 18" fill="none" className="mx-auto mb-2" data-id="element-155">
      <polygon points="8,0 16,4.5 16,13.5 8,18 0,13.5 0,4.5" fill="white" data-id="element-156" />
    </svg>;
}
function WavyUnderline() {
  return <svg className="w-full h-2 mt-0.5" viewBox="0 0 200 8" fill="none" preserveAspectRatio="none" data-id="element-157">
      <path d="M0,4 Q10,0 20,4 T40,4 T60,4 T80,4 T100,4 T120,4 T140,4 T160,4 T180,4 T200,4" stroke="#FF1493" strokeWidth="2.5" fill="none" data-id="element-158" />
    </svg>;
}
interface ServiceCardProps {
  title: string;
  image: string;
  className?: string;
  imageClassName?: string;
  disableHoverZoom?: boolean;
}
function ServiceCard({
  title,
  image,
  className = '',
  imageClassName = '',
  disableHoverZoom = false
}: ServiceCardProps) {
  return <div className={`group relative overflow-hidden rounded-lg cursor-pointer ${className}`} data-id="element-159">
      <img src={image} alt={title} className={`h-full w-full object-cover transition-transform duration-500 ${!disableHoverZoom ? 'group-hover:scale-[1.03]' : ''} ${imageClassName}`} data-id="element-160" />
      {/* Dark gradient overlay at bottom */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-161"></div>
      {/* Pink hover overlay */}
      <div className="absolute inset-0 bg-[#FF1493]/0 group-hover:bg-[#FF1493]/10 transition-colors duration-500" data-id="element-162"></div>
      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6 text-center" data-id="element-163">
        <HexIcon data-id="element-164" />
        <h3 className="text-base sm:text-lg lg:text-xl uppercase text-white tracking-wider font-[600] font-[Poppins,_sans-serif]" data-id="element-165">
          {title}
        </h3>
      </div>
    </div>;
}
export function ServicesSection() {
  return <section id="treatments" className="bg-black py-16 sm:py-20 lg:py-28" data-id="element-166">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8" data-id="element-167">
        {/* Header */}
        <AnimateOnScroll className="text-center mb-16" data-id="element-168">
          <p className="font-serif italic text-base text-[#B0B0B0] mb-4" data-id="element-169">
            (Be Happy :)
          </p>
          <h2 className="font-heading text-4xl lg:text-5xl font-bold uppercase text-white mb-4" data-id="element-170">
            <span className="inline-block" data-id="element-171">
              <span className="text-[#FF1493]" data-id="element-172">ELEVATE</span>
              <WavyUnderline data-id="element-173" />
            </span>{' '}
            YOUR BEAUTY
            <br data-id="element-174" />
            AND WELLNESS
          </h2>
          <p className="font-body text-base text-[#B0B0B0] max-w-xl mx-auto" data-id="element-175">
            Experience personalization at its finest with our selection of
            aesthetic and medical treatments.
          </p>
        </AnimateOnScroll>

        {/* Asymmetric Grid */}
        <AnimateOnScroll delay={200} data-id="element-176">
          {/* Desktop Grid */}
          <div className="hidden md:grid grid-cols-3 grid-rows-2 gap-2 lg:gap-3 aspect-[3/2]" data-id="element-177">
            {/* Top-left: Body Contouring */}
            <ServiceCard title="Body Contouring Treatments" image="https://cdn.magicpatterns.com/uploads/rxM463Fwok35NXBiUYCsj1/Group_191.png" className="col-start-1 row-start-1" data-id="element-178" />

            {/* Center: Laser Treatments (tall, spans both rows) */}
            <ServiceCard title="Laser Treatments" image="https://cdn.magicpatterns.com/uploads/mDdEBxNAdQwE4zfEdVmsx7/Group_193.png" className="col-start-2 row-start-1 row-span-2" imageClassName="-scale-x-100" disableHoverZoom={true} data-id="element-179" />

            {/* Top-right: Injectables */}
            <ServiceCard title="Injectables" image="https://cdn.magicpatterns.com/uploads/h3tgiifQegxufPUGkmncDJ/Group_194.png" className="col-start-3 row-start-1" data-id="element-180" />

            {/* Bottom-left: Skin Services */}
            <ServiceCard title="Skin Services" image="https://cdn.magicpatterns.com/uploads/2hvJggZ8FFcyD1MmdKAGao/Group_192.png" className="col-start-1 row-start-2" data-id="element-181" />

            {/* Bottom-right: Medical Treatments */}
            <ServiceCard title="Medical Treatments" image="https://cdn.magicpatterns.com/uploads/5mQcfpauLmuzGyJRdkz4oN/Group_195.png" className="col-start-3 row-start-2" data-id="element-182" />
          </div>

          {/* Mobile Stack */}
          <div className="md:hidden flex flex-col gap-3" data-id="element-183">
            <ServiceCard title="Body Contouring Treatments" image="https://cdn.magicpatterns.com/uploads/rxM463Fwok35NXBiUYCsj1/Group_191.png" className="aspect-[4/3]" data-id="element-184" />
            <ServiceCard title="Laser Treatments" image="https://cdn.magicpatterns.com/uploads/mDdEBxNAdQwE4zfEdVmsx7/Group_193.png" className="aspect-[4/3]" imageClassName="-scale-x-100" disableHoverZoom={true} data-id="element-185" />
            <ServiceCard title="Injectables" image="https://cdn.magicpatterns.com/uploads/h3tgiifQegxufPUGkmncDJ/Group_194.png" className="aspect-[4/3]" data-id="element-186" />
            <ServiceCard title="Skin Services" image="https://cdn.magicpatterns.com/uploads/2hvJggZ8FFcyD1MmdKAGao/Group_192.png" className="aspect-[4/3]" data-id="element-187" />
            <ServiceCard title="Medical Treatments" image="https://cdn.magicpatterns.com/uploads/5mQcfpauLmuzGyJRdkz4oN/Group_195.png" className="aspect-[4/3]" data-id="element-188" />
          </div>
        </AnimateOnScroll>
      </div>
    </section>;
}